/**
 * Customer Portal Dashboard Selectors
 * Memoized state queries
 */

import { createSelector } from '@reduxjs/toolkit'
import type { CustomerPortalDashboardState } from '../domain/types'
import { resolveDateRange, formatDateRangeDisplay } from '../domain/utils'

// ============================================
// Root State Type
// ============================================

interface RootState {
  customerPortalDashboard: CustomerPortalDashboardState
}

// ============================================
// Base Selector
// ============================================

const selectDashboardState = (state: RootState) => state.customerPortalDashboard

// ============================================
// Data Selectors
// ============================================

export const selectDashboardData = createSelector(
  selectDashboardState,
  (state) => state.data
)

export const selectBalanceKpi = createSelector(
  selectDashboardData,
  (data) => data?.balance ?? null
)

export const selectOrdersKpi = createSelector(
  selectDashboardData,
  (data) => data?.orders ?? null
)

export const selectDeliveriesKpi = createSelector(
  selectDashboardData,
  (data) => data?.deliveries ?? null
)

export const selectFinancialKpi = createSelector(
  selectDashboardData,
  (data) => data?.financial ?? null
)

export const selectSummaryKpi = createSelector(
  selectDashboardData,
  selectDashboardState,
  (data, state) => state.rankingsData?.summary ?? data?.summary ?? null
)

// ============================================
// Balance Specific Selectors
// ============================================

export const selectCreditLimit = createSelector(
  selectBalanceKpi,
  (balance) => balance?.creditLimit ?? 0
)

export const selectUsedBalance = createSelector(
  selectBalanceKpi,
  (balance) => balance?.usedBalance ?? 0
)

export const selectCreditUtilization = createSelector(
  selectBalanceKpi,
  (balance) => {
    if (!balance || balance.creditLimit === 0) return 0
    return (balance.usedBalance / balance.creditLimit) * 100
  }
)

export const selectAccountStatus = createSelector(
  selectBalanceKpi,
  (balance) => balance?.accountStatus ?? 'active'
)

// ============================================
// Orders Specific Selectors
// ============================================

export const selectTotalOrders = createSelector(
  selectOrdersKpi,
  (orders) => orders?.total ?? 0
)

export const selectPendingOrders = createSelector(
  selectOrdersKpi,
  (orders) => orders?.pending ?? 0
)

export const selectProcessingOrders = createSelector(
  selectOrdersKpi,
  (orders) => orders?.processing ?? 0
)

export const selectDeliveredOrders = createSelector(
  selectOrdersKpi,
  (orders) => orders?.delivered ?? 0
)

// ============================================
// Deliveries Specific Selectors
// ============================================

export const selectTotalTonsDelivered = createSelector(
  selectSummaryKpi,
  selectDeliveriesKpi,
  (summary, deliveries) => summary?.totalQuantityTons ?? deliveries?.totalTons ?? 0
)

export const selectDeliveryCount = createSelector(
  selectDeliveriesKpi,
  (deliveries) => deliveries?.deliveryCount ?? 0
)

export const selectCitiesCount = createSelector(
  selectSummaryKpi,
  (summary) => summary?.citiesCount ?? 0
)

export const selectCustomersCount = createSelector(
  selectSummaryKpi,
  (summary) => summary?.customersCount ?? 0
)

// ============================================
// Financial Specific Selectors
// ============================================

export const selectTotalReceivable = createSelector(
  selectFinancialKpi,
  (financial) => financial?.totalReceivable ?? { amount: 0, currency: 'SAR' as const }
)

export const selectOverdueInvoices = createSelector(
  selectFinancialKpi,
  (financial) => financial?.overdueInvoices ?? 0
)

export const selectHasOverdueInvoices = createSelector(
  selectOverdueInvoices,
  (count) => count > 0
)

// ============================================
// Date Range Selectors
// ============================================

export const selectDateRange = createSelector(
  selectDashboardState,
  (state) => state.dateRange
)

export const selectDatePreset = createSelector(
  selectDateRange,
  (dateRange) => dateRange.preset
)

export const selectResolvedDateRange = createSelector(
  selectDateRange,
  (dateRange) => resolveDateRange(dateRange)
)

export const selectFormattedDateRange = createSelector(
  selectDateRange,
  (dateRange) => formatDateRangeDisplay(dateRange)
)

// ============================================
// Loading/Error Selectors
// ============================================

export const selectDashboardLoading = createSelector(
  selectDashboardState,
  (state) => state.loading
)

export const selectIsInitialLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.initial
)

export const selectIsRefreshing = createSelector(
  selectDashboardLoading,
  (loading) => loading.refreshing
)

export const selectIsLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.initial || loading.refreshing
)

export const selectDashboardError = createSelector(
  selectDashboardState,
  (state) => state.error
)

export const selectHasError = createSelector(
  selectDashboardError,
  (error) => error.message !== null
)

export const selectErrorMessage = createSelector(
  selectDashboardError,
  (error) => error.message
)

// ============================================
// SAP Balance Selectors
// ============================================

export const selectSapBalance = createSelector(
  selectDashboardState,
  (state) => state.sapBalance
)

export const selectSapBalanceLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.sapBalance
)

/** Available balance from SAP contractBalances (sum of totalNetAmount across all contracts) */
export const selectContractBalance = createSelector(
  selectSapBalance,
  (sap) => {
    if (!sap?.contractBalances?.length) return null
    return sap.contractBalances.reduce((sum, c) => sum + (c.totalNetAmount ?? 0), 0)
  }
)

export const selectAvailableBalance = createSelector(
  selectContractBalance,
  selectBalanceKpi,
  (contractBalance, balance) => contractBalance ?? balance?.availableBalance ?? 0
)

// ============================================
// Meta Selectors
// ============================================

export const selectLastRefreshed = createSelector(
  selectDashboardState,
  (state) => state.lastRefreshed
)

export const selectHasData = createSelector(
  selectDashboardData,
  (data) => data !== null
)

// ============================================
// Chart Data Selectors
// ============================================

export const selectChartData = createSelector(
  selectDashboardState,
  (state) => state.chartData
)

export const selectChartLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.chart
)

export const selectChartTimeSeries = createSelector(
  selectChartData,
  (chart) => chart?.timeSeries ?? []
)

export const selectChartPeriod = createSelector(
  selectChartData,
  (chart) => chart?.period ?? 'monthly'
)

// ============================================
// Rankings Selectors
// ============================================

export const selectRankingsData = createSelector(
  selectDashboardState,
  (state) => state.rankingsData
)

export const selectRankingsLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.rankings
)

export const selectTopProducts = createSelector(
  selectRankingsData,
  (rankings) => rankings?.topProducts ?? []
)

export const selectTopProductsByPackaging = createSelector(
  selectRankingsData,
  (rankings) => rankings?.topProductsByPackaging ?? []
)

export const selectTopCustomers = createSelector(
  selectRankingsData,
  (rankings) => rankings?.topCustomers ?? []
)

export const selectTopCities = createSelector(
  selectRankingsData,
  (rankings) => rankings?.topCities ?? []
)

export const selectTopFactories = createSelector(
  selectRankingsData,
  (rankings) => rankings?.topFactories ?? []
)

// ============================================
// Factory Detail Selectors
// ============================================

export const selectFactoryDetail = createSelector(
  selectDashboardState,
  (state) => state.factoryDetail
)

export const selectFactoryDetailData = createSelector(
  selectFactoryDetail,
  (detail) => detail?.data ?? null
)

export const selectFactoryDetailLoading = createSelector(
  selectFactoryDetail,
  (detail) => detail?.loading ?? false
)

export const selectFactoryDetailSummary = createSelector(
  selectFactoryDetailData,
  (data) => data?.summary ?? null
)

// ============================================
// Entity Detail Selectors (customer / product / city)
// ============================================

export const selectEntityDetail = createSelector(
  selectDashboardState,
  (state) => state.entityDetail
)

export const selectEntityDetailData = createSelector(
  selectEntityDetail,
  (detail) => detail?.data ?? null
)

export const selectEntityDetailLoading = createSelector(
  selectEntityDetail,
  (detail) => detail?.loading ?? false
)

export const selectEntityDetailType = createSelector(
  selectEntityDetail,
  (detail) => detail?.entityType ?? null
)

export const selectEntityDetailName = createSelector(
  selectEntityDetail,
  (detail) => detail?.entityName ?? null
)

// ============================================
// Live Feed Selectors
// ============================================

export const selectLiveFeedData = createSelector(
  selectDashboardState,
  (state) => state.liveFeed
)

export const selectLiveFeedLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.liveFeed
)

export const selectLiveFeedItems = createSelector(
  selectLiveFeedData,
  (feed) => feed?.items ?? []
)

export const selectTotalAtFactory = createSelector(
  selectLiveFeedData,
  (feed) => feed?.totalAtFactory ?? 0
)

// ============================================
// Orders Value Selectors
// ============================================

export const selectOrdersValueKpi = createSelector(
  selectDashboardState,
  (state) => state.ordersValue
)

export const selectOrdersValueLoading = createSelector(
  selectDashboardLoading,
  (loading) => loading.ordersValue
)

export const selectTotalOrdersValueWithTax = createSelector(
  selectOrdersValueKpi,
  (kpi) => kpi?.totalOrdersValueWithTax ?? 0
)

export const selectTotalOrdersValueBeforeTax = createSelector(
  selectOrdersValueKpi,
  (kpi) => kpi?.totalOrdersValueBeforeTax ?? 0
)

export const selectOrdersValueCurrency = createSelector(
  selectOrdersValueKpi,
  (kpi) => kpi?.currency ?? 'SAR'
)

/**
 * Difference between customer balance (SAP contractBalances) and total orders value incl. tax.
 * Positive: balance exceeds outstanding orders value.
 * Negative: orders value exceeds available balance.
 */
export const selectBalanceVsOrdersDiff = createSelector(
  selectContractBalance,
  selectBalanceKpi,
  selectOrdersValueKpi,
  (contractBalance, balance, ordersValue) => {
    const available = contractBalance ?? balance?.availableBalance ?? 0
    const totalWithTax = ordersValue?.totalOrdersValueWithTax ?? 0
    return available - totalWithTax
  }
)
